// ui/confirmModal.js
// Reusable styled confirmation modal

let modalElement = null;
let resolvePromise = null;

/**
 * Show a styled confirmation modal
 * @param {Object} options - Modal options
 * @param {string} options.title - Modal title
 * @param {string} options.message - Confirmation message
 * @param {string} options.confirmText - Confirm button text (default: 'Confirm')
 * @param {string} options.cancelText - Cancel button text (default: 'Cancel')
 * @param {string} options.confirmClass - Additional class for confirm button (e.g., 'danger')
 * @returns {Promise<boolean>} - Resolves true if confirmed, false if cancelled
 */
export function showConfirmModal(options = {}) {
    const {
        title = 'Confirm',
        message = 'Are you sure?',
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        confirmClass = ''
    } = options;

    return new Promise((resolve) => {
        resolvePromise = resolve;

        const html = `
            <div class="modal-overlay confirm-modal-overlay" id="confirm-modal-overlay">
                <div class="modal-content confirm-modal">
                    <div class="modal-header">
                        <h2>${title}</h2>
                    </div>
                    <div class="modal-body">
                        <p class="confirm-message">${message}</p>
                    </div>
                    <div class="modal-footer confirm-buttons">
                        <button class="btn btn-secondary" onclick="window.confirmModal.cancel()">
                            ${cancelText}
                        </button>
                        <button class="btn btn-primary ${confirmClass}" onclick="window.confirmModal.confirm()">
                            ${confirmText}
                        </button>
                    </div>
                </div>
            </div>
        `;

        // Remove existing modal if any
        if (modalElement) {
            modalElement.remove();
        }

        // Create and append modal
        modalElement = document.createElement('div');
        modalElement.innerHTML = html;
        document.body.appendChild(modalElement);

        // Add fade-in animation
        requestAnimationFrame(() => {
            const overlay = document.getElementById('confirm-modal-overlay');
            if (overlay) overlay.classList.add('active');
        });

        // Close on overlay click (outside modal)
        const overlay = document.getElementById('confirm-modal-overlay');
        if (overlay) {
            overlay.addEventListener('click', (e) => {
                if (e.target === overlay) {
                    window.confirmModal.cancel();
                }
            });
        }

        // Close on Escape key
        document.addEventListener('keydown', handleKeydown);
    });
}

function handleKeydown(e) {
    if (e.key === 'Escape') {
        window.confirmModal.cancel();
    } else if (e.key === 'Enter') {
        window.confirmModal.confirm();
    }
}

function closeModal(result) {
    document.removeEventListener('keydown', handleKeydown);

    const overlay = document.getElementById('confirm-modal-overlay');
    if (overlay) {
        overlay.classList.remove('active');
        overlay.classList.add('closing');

        // Wait for animation then remove
        setTimeout(() => {
            if (modalElement) {
                modalElement.remove();
                modalElement = null;
            }
        }, 200);
    }

    if (resolvePromise) {
        resolvePromise(result);
        resolvePromise = null;
    }
}

// Expose to window for onclick handlers
window.confirmModal = {
    confirm: () => closeModal(true),
    cancel: () => closeModal(false)
};

export default { showConfirmModal };
